import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { CalendarStatusType, ListType } from "src/helpers/Enums";
import { useNavigate } from "react-router-dom";

const TOOLTIP_ARROW_GAP = 12;

const Box = (props) => {
  const [creativeTooltipVisible, setCreativeTooltipVisible] = useState(false);
  const [tooltipAnchorRect, setTooltipAnchorRect] = useState(null);
  const [copiedCreativeField, setCopiedCreativeField] = useState(null);
  const [idTooltipVisible, setIdTooltipVisible] = useState(false);
  const [idTooltipAnchorRect, setIdTooltipAnchorRect] = useState(null);
  const [idCopied, setIdCopied] = useState(false);
  const creativeTooltipCloseTimeoutRef = useRef(null);
  const creativeNameRef = useRef(null);
  const strategyIdButtonRef = useRef(null);
  const done = props.item?.status === CalendarStatusType.DONE;
  const today = new Date();
  const navigate = useNavigate();
  const idTooltipRef = useRef(null);

  const handleCopyId = () => {
    const id = props.item?._id;
    if (id) {
      navigator.clipboard.writeText(id);
      setIdCopied(true);
    }
  };

  const handleCopyCreativeField = async (field, value) => {
    try {
      await navigator.clipboard.writeText(value ?? "");
      setCopiedCreativeField(field);
    } catch (error) {
      setCopiedCreativeField(null);
    }
  };

  useEffect(() => {
    if (!idTooltipVisible) return;
    const handleClickOutside = (e) => {
      if (
        strategyIdButtonRef.current?.contains(e.target) ||
        idTooltipRef.current?.contains(e.target)
      )
        return;
      setIdTooltipVisible(false);
      setIdTooltipAnchorRect(null);
      setIdCopied(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [idTooltipVisible]);

  return (
    <div className="SMSEmailBox">
      <div
        className={"SMSEmailBoxCompleteDivider"}
        style={{
          backgroundColor: done
            ? "#58AD2F"
            : props.item?.status === CalendarStatusType.CREATED
            ? "#7E50EA"
            : "#DD2E2E",
        }}
      />
      <div className={"w-full flex flex-col gap-2"}>
        <div className={"flex gap-2"}>
          <div
            className={"ListStatusBox"}
            style={{
              backgroundColor: done
                ? "#58AD2F"
                : props.item?.status === CalendarStatusType.CREATED
                ? "#7E50EA"
                : "#DD2E2E",
            }}
          >
            <img
              alt={"Logo"}
              src={
                done
                  ? "/media/icons/completedIcon.svg"
                  : props.item?.status === CalendarStatusType.CREATED
                  ? "/media/icons/createdIcon.svg"
                  : "/media/icons/scheduledIcon.svg"
              }
              className={"w-[14px] h-[14px]"}
            />
            {done ? "completed" : props.item?.status?.toLowerCase()}
          </div>
          <div
            className={"SMSEmailBoxBox"}
            style={{
              backgroundColor: done
                ? "#E6F3E0"
                : props.item?.status === CalendarStatusType.CREATED
                ? "#EDE5FF"
                : "#FED9D9",
            }}
          >
            {props.item?.list_id?.type}
          </div>
          {props.item?.list_id?.type === ListType.Email && (
            <div className={"CalendarSegmentBox"}>
              <div className={"flex gap-1 items-center"}>
                <div className={"DashboardListNameTitle"}>Segment:</div>
                <div className={"SMSEmailBoxListName"}>{}</div>
                <div className="flex gap-1 items-center text-black text-xs font-medium">
                  <div
                    className={
                      props.item?.list_id?.segment === "Live"
                        ? "ListSegmentLive"
                        : "ListSegmentRepair"
                    }
                  ></div>
                  {props.item?.list_id?.segment}
                </div>
              </div>
            </div>
          )}
          <div className={"BoxDescription"}>
            {done
              ? "All information entries for this list have been completed."
              : "Revenue input is required for this list."}
          </div>
        </div>
        <div className={"CalendarDetailInfoBox"}>
          <div className={"flex gap-1 items-center"}>
            <img
              alt={"Logo"}
              src={"/media/icons/rows.svg"}
              className={"w-[14px] h-[14px]"}
            />
            <div className={"DashboardListNameTitle"}>List Name:</div>
            <div className={"SMSEmailBoxListName"}>
              {props.item?.list_id?.name}
            </div>
          </div>
          <div className={"HorizontalDivider"}></div>
          <div className={"flex gap-1 items-center"}>
            <img
              alt={"Logo"}
              src={"/media/icons/offer.svg"}
              className={"w-[14px] h-[14px]"}
            />
            <div className={"DashboardListNameTitle"}>Offer Name:</div>
            <div className={"flex gap-3"}>
              <div className={"SMSEmailBoxListName"}>
                {props.item?.offer_id?.name}
              </div>
              {done && (
                <div className="flex h-[18px] px-[6px] py-0 items-center gap-1 rounded-[34px] bg-[#8185A8] text-white font-[Inter] text-[11px] font-semibold leading-normal whitespace-nowrap">
                  Offer Score: {props.item?.score?.toFixed(2)}
                </div>
              )}
            </div>
          </div>
          <div>
            <div className={"HorizontalDivider"}></div>
            <div className={"flex gap-1 items-center"}>
              <img
                alt={"Logo"}
                src={"/media/icons/creative.svg"}
                className={"w-[14px] h-[14px]"}
              />
              <div className={"DashboardListNameTitle"}>Creative Name:</div>
              <div className="relative inline-block">
                <span
                  ref={creativeNameRef}
                  className={`SMSEmailBoxListName inline-block select-none transition-colors ${
                    creativeTooltipVisible ? "CreativeNameLinkHover" : ""
                  }`}
                  onMouseEnter={() => {
                    clearTimeout(creativeTooltipCloseTimeoutRef.current);
                    if (creativeNameRef.current) {
                      const rect =
                        creativeNameRef.current.getBoundingClientRect();
                      setTooltipAnchorRect({
                        left: rect.left,
                        top: rect.top,
                        width: rect.width,
                      });
                      setCreativeTooltipVisible(true);
                    }
                  }}
                  onMouseLeave={() => {
                    creativeTooltipCloseTimeoutRef.current = setTimeout(() => {
                      setCreativeTooltipVisible(false);
                      setTooltipAnchorRect(null);
                      setCopiedCreativeField(null);
                    }, 120);
                  }}
                  role="text"
                >
                  {props.item?.creative_id?.name}
                </span>
                {creativeTooltipVisible &&
                  tooltipAnchorRect &&
                  createPortal(
                    <div
                      className="CreativeNameTooltip z-[9999]"
                      style={{
                        position: "fixed",
                        left:
                          tooltipAnchorRect.left + tooltipAnchorRect.width / 2,
                        top: tooltipAnchorRect.top,
                        transform: `translate(-50%, calc(-100% - ${TOOLTIP_ARROW_GAP}px))`,
                      }}
                      onMouseEnter={() => {
                        clearTimeout(creativeTooltipCloseTimeoutRef.current);
                        setCreativeTooltipVisible(true);
                      }}
                      onMouseLeave={() => {
                        setCreativeTooltipVisible(false);
                        setTooltipAnchorRect(null);
                        setCopiedCreativeField(null);
                      }}
                    >
                      <div className="flex flex-col items-start gap-2 self-stretch">
                        <div className="mb-1.5 flex items-center gap-1.5 font-semibold">
                          <img
                            alt=""
                            src="/media/icons/tooltip-eye.svg"
                            className="h-[14px] w-[14px]"
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                          Preview
                        </div>
                        <div className="h-[1px] self-stretch bg-white opacity-20"></div>
                      </div>
                      <div className="flex flex-col items-start gap-2 self-stretch pt-2 pb-3">
                        <div className="flex items-start gap-[10px] self-stretch">
                          <div className="min-w-[60px] text-white font-[Inter] text-[11px] font-semibold leading-[18px]">
                            Name
                          </div>
                          <div className="text-white font-[Inter] text-[11px] font-semibold leading-[18px]">
                            :
                          </div>
                          <div className="flex items-start justify-between gap-2 flex-1">
                            <div className="text-white font-[Inter] text-[11px] font-medium leading-[18px] break-all">
                              {props.item?.creative_id?.name}
                            </div>
                            <button
                              type="button"
                              className={
                                copiedCreativeField === "name"
                                  ? "StrategyIdTooltipCopiedButton !h-[24px] !w-[76px] !px-2 !text-[11px]"
                                  : "StrategyIdTooltipCopyButton !h-[24px] !w-[76px] !px-2 !text-[11px]"
                              }
                              onClick={() =>
                                handleCopyCreativeField(
                                  "name",
                                  props.item?.creative_id?.name
                                )
                              }
                            >
                              {copiedCreativeField === "name"
                                ? "Copied"
                                : "Copy"}
                            </button>
                          </div>
                        </div>
                        <div className="h-[1px] self-stretch bg-white opacity-20"></div>
                        <div className="flex items-start gap-[10px] self-stretch">
                          <div className="min-w-[60px] text-white font-[Inter] text-[11px] font-semibold leading-[18px]">
                            Message
                          </div>
                          <div className="text-white font-[Inter] text-[11px] font-semibold leading-[18px]">
                            :
                          </div>
                          <div className="flex items-start justify-between gap-2 flex-1">
                            <div className="text-white font-[Inter] text-[11px] font-medium leading-[18px] break-all">
                              {props.item?.creative_id?.note}
                            </div>
                            <button
                              type="button"
                              className={
                                copiedCreativeField === "message"
                                  ? "StrategyIdTooltipCopiedButton !h-[24px] !w-[76px] !px-2 !text-[11px]"
                                  : "StrategyIdTooltipCopyButton !h-[24px] !w-[76px] !px-2 !text-[11px]"
                              }
                              onClick={() =>
                                handleCopyCreativeField(
                                  "message",
                                  props.item?.creative_id?.note
                                )
                              }
                            >
                              {copiedCreativeField === "message"
                                ? "Copied"
                                : "Copy"}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>,
                    document.body
                  )}
              </div>
            </div>
          </div>
          {idTooltipVisible &&
            idTooltipAnchorRect &&
            createPortal(
              <div
                ref={idTooltipRef}
                className="StrategyIdTooltip StrategyIdTooltipSide z-[9999]"
                style={{
                  position: "fixed",
                  left: idTooltipAnchorRect.left - TOOLTIP_ARROW_GAP,
                  top: idTooltipAnchorRect.top + idTooltipAnchorRect.height / 2,
                  transform: "translate(-100%, -50%)",
                }}
              >
                <div className="StrategyIdTooltipHeader">
                  <div className="flex items-center gap-1">
                    <img
                      alt=""
                      src="/media/icons/code-02-white.svg"
                      className="h-[14px] w-[14px]"
                    />
                    <div className="text-white font-['Inter'] text-xs font-semibold leading-[18px]">
                      ID
                    </div>
                  </div>
                  <div className="h-[1px] self-stretch bg-white opacity-20"></div>
                </div>
                <div className="flex flex-col items-start gap-2 py-2 self-stretch">
                  <div className="flex items-center gap-[10px] self-stretch">
                    <div className="text-white font-['Inter'] text-xs font-medium leading-[18px] flex-[1_0_0]">
                      {props.item?._id ?? "—"}
                    </div>
                    <button
                      type="button"
                      className={
                        idCopied
                          ? "StrategyIdTooltipCopiedButton"
                          : "StrategyIdTooltipCopyButton"
                      }
                      onClick={handleCopyId}
                    >
                      {idCopied ? (
                        <>
                          <img
                            alt=""
                            src="/media/icons/check-circle-white.svg"
                            className="h-[14px] w-[14px]"
                          />
                          Copied
                        </>
                      ) : (
                        <>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="none"
                          >
                            <g clipPath="url(#clip0_3753_45955)">
                              <path
                                d="M4.66797 4.6665V3.03317C4.66797 2.37978 4.66797 2.05308 4.79513 1.80352C4.90698 1.58399 5.08546 1.40552 5.30498 1.29366C5.55454 1.1665 5.88124 1.1665 6.53464 1.1665H10.968C11.6214 1.1665 11.9481 1.1665 12.1976 1.29366C12.4171 1.40552 12.5956 1.58399 12.7075 1.80352C12.8346 2.05308 12.8346 2.37978 12.8346 3.03317V7.4665C12.8346 8.1199 12.8346 8.4466 12.7075 8.69616C12.5956 8.91568 12.4171 9.09416 12.1976 9.20601C11.9481 9.33317 11.6214 9.33317 10.968 9.33317H9.33464M3.03464 12.8332H7.46797C8.12136 12.8332 8.44806 12.8332 8.69762 12.706C8.91715 12.5942 9.09562 12.4157 9.20748 12.1962C9.33464 11.9466 9.33464 11.6199 9.33464 10.9665V6.53317C9.33464 5.87978 9.33464 5.55308 9.20748 5.30352C9.09562 5.08399 8.91715 4.90552 8.69762 4.79366C8.44806 4.6665 8.12136 4.6665 7.46797 4.6665H3.03464C2.38124 4.6665 2.05454 4.6665 1.80498 4.79366C1.58546 4.90552 1.40698 5.08399 1.29513 5.30352C1.16797 5.55308 1.16797 5.87978 1.16797 6.53317V10.9665C1.16797 11.6199 1.16797 11.9466 1.29513 12.1962C1.40698 12.4157 1.58546 12.5942 1.80498 12.706C2.05454 12.8332 2.38124 12.8332 3.03464 12.8332Z"
                                stroke="white"
                                strokeWidth="1.33"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_3753_45955">
                                <rect width="14" height="14" fill="white" />
                              </clipPath>
                            </defs>
                          </svg>
                          Copy ID
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>,
              document.body
            )}
          {done && (
            <>
              <div className={"HorizontalDivider"}></div>
              <div className={"flex gap-1 items-center"}>
                <img
                  alt={"Logo"}
                  src={"/media/icons/currency-dollar-circle.svg"}
                  className={"w-[14px] h-[14px]"}
                />
                <div className={"DashboardListNameTitle"}>Revenue:</div>
                <div className={"SMSEmailBoxListName"}>
                  ${props.item?.revenue}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {props.item?.status === CalendarStatusType.CREATED ? (
        <div className={"flex flex-col gap-2"}>
          <button
            className={"ScheduleButton"}
            onClick={() => props.handleSchedulePopup(props.item, props.day)}
          >
            <img
              alt={"Logo"}
              src={"/media/icons/clock.svg"}
              className={"w-4 h-4"}
            />
            {"Schedule"}
          </button>
          <div className={"flex justify-center items-center"}>
            <button
              className="StrategyEditButton"
              onClick={() =>
                navigate("/specificCalendar", {
                  state: {
                    date: props.date,
                    item: props.item,
                  },
                })
              }
            >
              <img
                alt={"Logo"}
                src={"/media/icons/calendar.svg"}
                className={"w-4 h-4"}
              />
            </button>
            <button
              ref={strategyIdButtonRef}
              type="button"
              className={
                idTooltipVisible
                  ? "StrategyPressedIdButton"
                  : "StrategyIdButton"
              }
              onClick={() => {
                if (idTooltipVisible) {
                  setIdTooltipVisible(false);
                  setIdTooltipAnchorRect(null);
                  setIdCopied(false);
                } else if (strategyIdButtonRef.current) {
                  const rect =
                    strategyIdButtonRef.current.getBoundingClientRect();
                  setIdTooltipAnchorRect({
                    left: rect.left,
                    top: rect.top,
                    width: rect.width,
                    height: rect.height,
                  });
                  setIdTooltipVisible(true);
                }
              }}
            >
              <img
                alt={"Logo"}
                src={
                  idTooltipVisible
                    ? "/media/icons/code-02-gray.svg"
                    : "/media/icons/code-02.svg"
                }
                className={"w-4 h-4"}
              />
            </button>
            <button
              className="StrategyDeleteButton"
              onClick={() => props.handleDeleteSchedule(props.item)}
            >
              <img
                alt={"Logo"}
                src={"/media/icons/deleteIcon.svg"}
                className={"w-4 h-4"}
              />
            </button>
          </div>
        </div>
      ) : (
        <div className={"flex flex-col gap-2"}>
          {props.item?.status === CalendarStatusType.SCHEDULED &&
            today >= props.date && (
              <button
                className={"EnterRevenueButton"}
                onClick={() => props.handleRevenue(props.item, props.day)}
              >
                <img
                  alt={"Logo"}
                  src={"/media/icons/arrowRightRed.svg"}
                  className={"w-[14px] h-[14px]"}
                />
                {"Enter Revenue"}
              </button>
            )}
          {props.item?.status === CalendarStatusType.SCHEDULED ? (
            <div className={"flex justify-center items-center"}>
              <button
                className="StrategyEditButton"
                onClick={() =>
                  navigate("/specificCalendar", {
                    state: {
                      date: props.date,
                      item: props.item,
                    },
                  })
                }
              >
                <img
                  alt={"Logo"}
                  src={"/media/icons/calendar.svg"}
                  className={"w-4 h-4"}
                />
              </button>
              <button
                ref={strategyIdButtonRef}
                type="button"
                className={
                  idTooltipVisible
                    ? "StrategyPressedIdButton"
                    : "StrategyIdButton"
                }
                onClick={() => {
                  if (idTooltipVisible) {
                    setIdTooltipVisible(false);
                    setIdTooltipAnchorRect(null);
                    setIdCopied(false);
                  } else if (strategyIdButtonRef.current) {
                    const rect =
                      strategyIdButtonRef.current.getBoundingClientRect();
                    setIdTooltipAnchorRect({
                      left: rect.left,
                      top: rect.top,
                      width: rect.width,
                      height: rect.height,
                    });
                    setIdTooltipVisible(true);
                  }
                }}
              >
                <img
                  alt={"Logo"}
                  src={
                    idTooltipVisible
                      ? "/media/icons/code-02-gray.svg"
                      : "/media/icons/code-02.svg"
                  }
                  className={"w-4 h-4"}
                />
              </button>
              <button
                className="StrategyDeleteButton"
                onClick={() => props.handleCancelSchedule?.(props.item)}
              >
                <img
                  alt={"Logo"}
                  src={"/media/icons/deleteIcon.svg"}
                  className={"w-4 h-4"}
                />
              </button>
            </div>
          ) : (
            <div className={"flex justify-center items-center"}>
              <button
                className="StrategyEditButton"
                onClick={() =>
                  navigate("/specificCalendar", {
                    state: {
                      date: props.date,
                      item: props.item,
                    },
                  })
                }
              >
                <img
                  alt={"Logo"}
                  src={"/media/icons/calendar.svg"}
                  className={"w-[14px] h-[14px] min-w-[14px] min-h-[14px]"}
                />
              </button>
              <button
                ref={strategyIdButtonRef}
                type="button"
                className={
                  idTooltipVisible
                    ? "StrategyPressedIdButton"
                    : "StrategyIdButton"
                }
                onClick={() => {
                  if (idTooltipVisible) {
                    setIdTooltipVisible(false);
                    setIdTooltipAnchorRect(null);
                    setIdCopied(false);
                  } else if (strategyIdButtonRef.current) {
                    const rect =
                      strategyIdButtonRef.current.getBoundingClientRect();
                    setIdTooltipAnchorRect({
                      left: rect.left,
                      top: rect.top,
                      width: rect.width,
                      height: rect.height,
                    });
                    setIdTooltipVisible(true);
                  }
                }}
              >
                <img
                  alt={"Logo"}
                  src={
                    idTooltipVisible
                      ? "/media/icons/code-02-gray.svg"
                      : "/media/icons/code-02.svg"
                  }
                  className={"w-[14px] h-[14px] min-w-[14px] min-h-[14px]"}
                />
              </button>
              <button
                className="StrategyDeleteButton"
                onClick={() => props.handleCancelSchedule?.(props.item)}
              >
                <img
                  alt={"Logo"}
                  src={"/media/icons/deleteIcon.svg"}
                  className={"w-[14px] h-[14px] min-w-[14px] min-h-[14px]"}
                />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Box;
